import { useEffect, useState } from "react";
import { api } from "../api";
import { maskChange, nowForMask } from "../docs/mask";
import { ruDateTime, nowMsk } from "../docs/helpers";
import type { CaseRow, CaseType, SessionUser, Temp } from "../types";
import { useMessage } from "./Message";

const DATE_TIME_MASK = "99.99.9999, 99:99";

/** Right now in the shape the mask expects — what the clock icon writes. */
const nowStamp = () => nowForMask(DATE_TIME_MASK, nowMsk()) ?? "";

/** Port of the docs-case-add form in pat_click.php + ajax/case_add|case_save.php. */
export default function CaseForm({
  patId,
  type,
  row,
  user = null,
  onClose,
  onSaved,
}: {
  patId: number;
  type: CaseType;
  row: CaseRow | null;
  /** Права на «+»/«×» у вариантов описания; читать варианты можно всем. */
  user?: SessionUser | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const message = useMessage();
  const [number, setNumber] = useState("");
  const [about, setAbout] = useState("");
  // A case is added at the moment the patient is in front of you, so дата
  // поступления starts at now; an existing one keeps what is stored (below).
  const [dateIn, setDateIn] = useState(() => (row ? "" : nowStamp()));
  const [dateOut, setDateOut] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  /* --- варианты «Краткого описания»: Платно, ОМС, осм… — общий каталог --- */
  const [opts, setOpts] = useState<Temp[]>([]);
  const [sugOpen, setSugOpen] = useState(false);
  const canAddOpt = !!(user?.can_add_temp || user?.is_admin);
  const canDelOpt = !!(user?.can_del_temp || user?.is_admin);

  useEffect(() => {
    // Читают ВСЕ — регистратура и медсёстры заводят случаи чаще врачей, и
    // список «Платно / ОМС / осм» им нужен в первую очередь.
    api
      .temps("case_about")
      .then((r) => setOpts(r.temps))
      .catch(() => setOpts([]));
  }, []);

  useEffect(() => {
    if (!row) return;
    setNumber(row.number ? String(row.number) : "");
    setAbout(row.about ?? "");
    setDateIn(ruDateTime(row.date_in));
    setDateOut(ruDateTime(row.date_out));
  }, [row]);

  async function addOption() {
    const value = about.trim();
    if (!value) {
      message("Сначала введите описание — оно и станет вариантом", "error");
      return;
    }
    if (opts.some((t) => t.text.trim().toLowerCase() === value.toLowerCase())) {
      message("Такой вариант уже есть", "error");
      return;
    }
    try {
      const created = await api.createTemp("case_about", value.slice(0, 100), value);
      setOpts((p) => [...p, created]);
      message("Вариант сохранён");
    } catch (err) {
      message(err as Error);
    }
  }

  async function delOption(t: Temp) {
    try {
      await api.deleteTemp(t.temp_id);
      setOpts((p) => p.filter((x) => x.temp_id !== t.temp_id));
      message("Вариант удалён");
    } catch (err) {
      message(err as Error);
    }
  }

  async function submit() {
    if (busy) return;
    setError("");
    if (!dateIn.trim()) {
      const text = type === "a" ? "Укажите дату заполнения медкарты" : "Укажите дату поступления";
      setError(text);
      message(text, "error");
      return;
    }
    setBusy(true);
    try {
      const body = { pat_id: patId, type, number, about, date_in: dateIn, date_out: dateOut };
      if (row) await api.updateCase(row.case_id, body);
      else await api.createCase(body);
      onSaved();
    } catch (err) {
      setError((err as Error).message);
      message(err as Error);
    } finally {
      setBusy(false);
    }
  }

  const q = about.trim().toLowerCase();
  const hits = q ? opts.filter((t) => t.text.toLowerCase().includes(q)) : [];
  const rest = q ? opts.filter((t) => !t.text.toLowerCase().includes(q)) : opts;

  return (
    <div className="docs-case-add" style={{ display: "block" }}>
      <form className="docs-case-add-inner" id="case-add" onSubmit={(e) => e.preventDefault()}>
        <div className="close" onClick={onClose} />
        <div>
          <div className="docs-case-header">
            {row ? "Изменить" : "Добавить"} {type === "a" ? "амбулаторный" : "стационарный"} случай
          </div>
          <div className="input">
            <div>Номер</div>
            <input
              type="text"
              name="number"
              value={number}
              onChange={(e) => setNumber(e.target.value.replace(/\D/g, ""))}
            />
          </div>
        </div>
        <div className="input opt-field">
          <div>Краткое описание</div>
          <input
            type="text"
            name="case_about"
            autoComplete="off"
            value={about}
            onFocus={() => setSugOpen(true)}
            // Выбор закрыл список, фокус остался в поле — клик или набор открывают снова.
            onClick={() => setSugOpen(true)}
            onBlur={() => setSugOpen(false)}
            onChange={(e) => {
              setAbout(e.target.value);
              setSugOpen(true);
            }}
          />
          {canAddOpt && (
            <i
              className="opt-add"
              title="Сохранить введённое как вариант этого поля"
              onMouseDown={(e) => e.preventDefault()}
              onClick={addOption}
            >
              +
            </i>
          )}
          {sugOpen && opts.length > 0 && (
            <div className="opt-list">
              {[...hits, ...rest].map((t) => (
                <div
                  key={t.temp_id}
                  className={`opt-row${hits.includes(t) ? " opt-hit" : ""}`}
                  // mousedown: клик должен успеть раньше, чем blur закроет список.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    setAbout(t.text);
                    setSugOpen(false);
                  }}
                >
                  <span className="opt-row-text">{t.text}</span>
                  {canDelOpt && (
                    <i
                      className="opt-del"
                      title="Удалить этот вариант"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        void delOption(t);
                      }}
                    >
                      ×
                    </i>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        <div className={`pat-add-row${type === "s" ? " half" : ""}`}>
          <div className="input input-date-in">
            <div>{type === "a" ? "Дата заполнения медкарты" : "Дата поступления"}</div>
            <i title="Текущие дата и время" onClick={() => setDateIn(nowStamp())} />
            <input
              type="tel"
              className="mask-date-time"
              name="date_in"
              placeholder="01.01.2026, 09:00"
              value={dateIn}
              onChange={(e) => setDateIn(maskChange(e, DATE_TIME_MASK, dateIn))}
            />
          </div>
          {/* Ambulatory cases have no discharge date (legacy hid the field). */}
          <div className="input input-date-out" style={type === "a" ? { display: "none" } : undefined}>
            <div>Дата выписки</div>
            <i title="Текущие дата и время" onClick={() => setDateOut(nowStamp())} />
            <input
              type="tel"
              className="mask-date-time"
              name="date_out"
              placeholder="01.01.2026, 15:00"
              value={dateOut}
              onChange={(e) => setDateOut(maskChange(e, DATE_TIME_MASK, dateOut))}
            />
          </div>
        </div>
        {error && <div className="pat-add-warning" style={{ display: "block" }}>{error}</div>}
        <div className="center">
          <div className="button" onClick={submit}>
            {busy ? "…" : row ? "Сохранить" : "Добавить"}
          </div>
        </div>
      </form>
    </div>
  );
}
